import { useState, type FormEvent } from 'react';
import { FAQS } from '@/mocks/entertainment';

const FORM_URL = 'https://readdy.ai/api/form/dagk4pbep053ijub2bqg';

type Status = { type: 'idle' } | { type: 'ok'; msg: string } | { type: 'err'; msg: string };

export default function FaqSubscribe() {
  const [open, setOpen] = useState<number>(0);
  const [status, setStatus] = useState<Status>({ type: 'idle' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get('phone_alt') ?? '').trim();

    // honeypot hit: pretend success, never send
    if (honeypot.length > 0) {
      setStatus({ type: 'ok', msg: '已成功加入吃瓜速递，记得查收确认邮件～' });
      form.reset();
      return;
    }

    fd.delete('phone_alt');
    setSubmitting(true);
    setStatus({ type: 'idle' });
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        parsed?.message ||
        '';
      const isSpam = serverMsg.toLowerCase().includes('spam') || responseText.toLowerCase().includes('spam');
      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus({ type: 'ok', msg: '订阅成功！最新瓜点与名场面将第一时间送达。' });
        form.reset();
      } else {
        setStatus({ type: 'err', msg: serverMsg || '提交失败了，请稍后再试或检查邮箱格式。' });
      }
    } catch {
      setStatus({ type: 'err', msg: '网络开小差了，请稍后再试。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="faq" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-14">
        {/* FAQ */}
        <div>
          <p className="font-label text-sm tracking-[0.3em] text-primary-600">KNOW BEFORE YOU WATCH</p>
          <h3 className="mt-2 font-heading text-3xl font-black text-foreground-950">吃瓜小贴士</h3>
          <p className="mt-1.5 text-sm text-foreground-600">关于生图、路透与我们的内容原则，常见问题一次说清</p>

          <div className="mt-6 space-y-3">
            {FAQS.map((f, idx) => {
              const isOpen = open === idx;
              return (
                <div key={f.q} className="overflow-hidden rounded-2xl border border-background-200 bg-background-100/60">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : idx)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                  >
                    <span className="text-[15px] font-bold leading-6 text-foreground-950">{f.q}</span>
                    <span
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                        isOpen ? 'bg-primary-600 text-background-50' : 'bg-background-200 text-foreground-700'
                      }`}
                    >
                      <i className={`ri-add-line transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}></i>
                    </span>
                  </button>
                  {isOpen && <p className="px-5 pb-5 text-sm leading-7 text-foreground-700">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* subscribe form */}
        <div id="subscribe" className="lg:pl-6">
          <div className="sticky top-36 overflow-hidden rounded-3xl border border-background-200 bg-foreground-950 p-7 text-background-50 md:p-9">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-500 text-foreground-950">
              <i className="ri-mail-send-line text-2xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-3xl font-black text-background-50">吃瓜速递</h3>
            <p className="mt-2.5 text-sm leading-6 text-background-100/80">
              订阅后我们会按你关注的频道，把红毯生图、名场面与热剧动态汇总成一封邮件。重要场次还会附上直播预告，让你第一时间蹲到现场。
            </p>

            <form data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate={false}>
              <div>
                <label htmlFor="subscribe-email" className="mb-1.5 block text-xs font-semibold text-background-100/70">
                  你的邮箱
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  placeholder="name@example.com"
                  className="w-full rounded-xl border border-background-100/20 bg-background-100/10 px-4 py-3 text-sm text-background-50 outline-none transition-colors placeholder:text-background-100/40 focus:border-accent-400/60 focus:bg-background-100/15"
                />
              </div>
              <div>
                <label htmlFor="subscribe-channel" className="mb-1.5 block text-xs font-semibold text-background-100/70">
                  最关心的栏目
                </label>
                <select
                  id="subscribe-channel"
                  name="focus_channel"
                  defaultValue="红毯生图与现场"
                  className="w-full appearance-none rounded-xl border border-background-100/20 bg-background-100/10 px-4 py-3 text-sm text-background-50 outline-none focus:border-accent-400/60"
                >
                  <option className="text-foreground-950">红毯生图与现场</option>
                  <option className="text-foreground-950">综艺名场面</option>
                  <option className="text-foreground-950">热剧动态</option>
                  <option className="text-foreground-950">全都要</option>
                </select>
              </div>

              {/* honeypot */}
              <input
                type="text"
                name="phone_alt"
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-400 disabled:opacity-60"
              >
                {submitting ? '发送中…' : '订阅吃瓜速递'}
                <i className="ri-send-plane-line"></i>
              </button>

              {status.type === 'ok' && (
                <p className="flex items-start gap-2 rounded-xl bg-accent-500/15 px-4 py-3 text-sm text-accent-200">
                  <i className="ri-checkbox-circle-fill mt-0.5"></i> {status.msg}
                </p>
              )}
              {status.type === 'err' && (
                <p className="flex items-start gap-2 rounded-xl bg-primary-500/15 px-4 py-3 text-sm text-primary-200">
                  <i className="ri-error-warning-fill mt-0.5"></i> {status.msg}
                </p>
              )}
              <p className="text-xs leading-5 text-background-100/50">
                订阅即代表同意接收本站栏目邮件，可随时退订。我们不会把你的邮箱用于其他用途。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}